import { useState, useEffect } from "react";
import Head from "next/head";
import classNames from "classnames/bind";
import { ZoomCrossOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ZoomCrossOutlined";
import { MagnifyOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MagnifyOutlined";
import {
  GalleryObject,
  ProductImages,
  getSortedGalleryItems,
  VideoPreloadValue,
} from "../utils/utils.ts";
import { Video } from "@/types/prodcatBaseTypes.ts";
import styles from "./CarouselGallery.module.scss";
import { Carousel } from "../components/Carousel/Carousel";
import { dispatchGalleryMonetateQEvent } from "../components/Analytics/dispatchGalleryMonetateQEvent";
import { imageProfileGenerator } from "@/utils";
import { SocialProofMessages } from "../components/SocialProofMessages/SocialProofMessages";
import { ZoomModal } from "../components/ZoomModal/ZoomModal";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";
import { ErrorBoundary } from "@/utils/error/ErrorBoundary.tsx";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import { FeatureFlagValue } from "@/types/types";

import { RuntimeConfig } from "@/types/types";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { SocialProofMessagesProperties } from "@/components/Galleries/utils/utils.ts";
import { ImageElementRef } from "@/components/Galleries/components/GalleryImageElement/ImageElement/ImageElement.tsx";
const cx = classNames.bind(styles);

type Props = {
  title: string;
  images: ProductImages;
  videos: Video[];
  sizes: string;
  dataComponent?: string;
  hasSocialProofMessagesExperience?: boolean;
  socialProofMessagesProperties: SocialProofMessagesProperties;
  hasImmediateStateUpdates?: boolean;
  hasTransparentThumbnails?: boolean;
  hasDeferredMonetateQEvents?: boolean;
  hasDeferredIntersectionObserverCallback?: boolean;
  hasSuperZoom?: boolean;
  useFetchPriority?: boolean;
  useResponsiveImages?: boolean;
  singleItemCarousel?: boolean;
  showVideosOnModal?: boolean;
  carouselGalleryInpTest?: boolean;
  videoPreload: VideoPreloadValue;
  renderSection?: boolean;
  condensedGallery?: boolean;
  experiencesRawObject: Experiences;
  runtimeConfig: RuntimeConfig;
  amendCarouselZoomIcon?: FeatureFlagValue;
  ref?: React.RefObject<ImageElementRef>;
};

export const Counter = ({
  fullscreen,
  selectedInd,
  itemCount,
}: {
  fullscreen?: boolean;
  selectedInd: number;
  itemCount: number;
}) => {
  return (
    <div
      className={cx(styles.counter, {
        "counter--fullscreen": fullscreen,
      })}
    >
      {selectedInd + 1} / {itemCount}
    </div>
  );
};

export const CarouselGalleryUI = ({
  title,
  images,
  videos,
  sizes,
  dataComponent,
  hasSocialProofMessagesExperience,
  socialProofMessagesProperties,
  hasImmediateStateUpdates,
  hasTransparentThumbnails,
  hasDeferredMonetateQEvents,
  hasDeferredIntersectionObserverCallback,
  hasSuperZoom,
  useFetchPriority,
  useResponsiveImages,
  singleItemCarousel = false,
  showVideosOnModal = false,
  carouselGalleryInpTest = false,
  videoPreload,
  renderSection = true,
  condensedGallery = false,
  experiencesRawObject,
  runtimeConfig,
  amendCarouselZoomIcon = false,
}: Props) => {
  const [selectedInd, setSelectedInd] = useState(0);
  const [initialised, setInitialised] = useState(false);
  const [modalSelectedInd, setModalSelectedInd] = useState(0);
  const [zoomModalActive, setZoomModalActive] = useState(false);
  const { flags } = useStandardProps();
  const gridGalleryVideoLastPosition =
    flags.gridGalleryVideoLastPosition?.value === true;
  const modalGalleryImprovements =
    flags.modalGalleryImprovements?.value === true;
  const videoEnhancements = flags.videoEnhancements?.value === true;
  const useNewSocialProofMessagesPosition =
    flags.useNewSocialProofMessagesPosition?.value === true;
  const outputImages: GalleryObject[] = [
    images.primary,
    ...images.alternatives,
  ].map((image) => {
    return {
      type: "image",
      imageUrl: image.split("?")[0] || "",
      title,
      thumbnailUrl: formatAmplienceImageURL(` ${image}?wid=120&hei=160`),
    };
  });

  const outputVideos: GalleryObject[] = videos.map((video) => {
    const videoThumbnail = video.sources.find(
      (source) => source.key === "thumbnail",
    );

    const videoThumbnailBaseUrl = videoThumbnail ? videoThumbnail.url : "";

    const { baseUrl = "" } = getBaseUrlAndQuery(videoThumbnailBaseUrl);

    const videoTumbnailUrlWithParams = `${baseUrl}?wid=120&hei=160`;

    return {
      type: "video",
      imageUrl: baseUrl,
      thumbnailUrl: videoTumbnailUrlWithParams,
      videoUrl: video.sources.find((source) => source.key === "stream")?.url,
      posterUrl: video.sources.find((source) => source.key === "poster")?.url,
      title,
    };
  });

  let mediaItems = gridGalleryVideoLastPosition
    ? [...outputImages, ...outputVideos]
    : getSortedGalleryItems<GalleryObject>(outputImages, outputVideos);

  const { monetateId } = useMonetateId();
  if (singleItemCarousel) {
    mediaItems = mediaItems.slice(0, 1);
  }

  const showZoomModal = () => {
    const selectedMediaItem = mediaItems[selectedInd];

    const currentItemIndex =
      selectedMediaItem?.type === "video"
        ? outputImages.length // Videos are currently pushed to the end
        : outputImages.findIndex(
            ({ imageUrl }) => selectedMediaItem?.imageUrl === imageUrl,
          );

    setModalSelectedInd(modalGalleryImprovements ? currentItemIndex : 0);
    setZoomModalActive(true);
    dispatchGalleryMonetateQEvent(
      "ZoomIcon",
      undefined,
      undefined,
      carouselGalleryInpTest,
    );

    recordImpressionByParam(
      runtimeConfig?.monetateEnvironment,
      experiencesRawObject,
      "pdp-scaffold-gallery-zoom-modal",
      "true",
      monetateId,
    );
  };

  const closeZoomModal = () => {
    setZoomModalActive(false);
    dispatchGalleryMonetateQEvent(
      "CloseModal",
      "imageModal",
      undefined,
      carouselGalleryInpTest,
    );
  };

  useEffect(() => {
    setInitialised(true);
  }, []);

  const validZoomIconValue =
    amendCarouselZoomIcon === "newIcon" || amendCarouselZoomIcon === "noIcon";

  const carouselContent = (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={images.primary}
          imageSrcSet={imageProfileGenerator(images.primary)}
          imageSizes={sizes}
          fetchPriority={useFetchPriority ? "high" : "auto"}
        />
      </Head>
      <Carousel
        mediaItems={mediaItems}
        dataComponent={dataComponent}
        selectedInd={selectedInd}
        setSelectedInd={setSelectedInd}
        imageSizes={sizes}
        initialised={initialised}
        hasImmediateStateUpdates={hasImmediateStateUpdates}
        hasTransparentThumbnails={hasTransparentThumbnails}
        hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
        hasDeferredIntersectionObserverCallback={
          hasDeferredIntersectionObserverCallback
        }
        hasSuperZoom={hasSuperZoom}
        usePrefetching={useFetchPriority}
        useResponsiveImages={useResponsiveImages}
        carouselGalleryInpTest={carouselGalleryInpTest}
        videoPreload={videoPreload}
        condensedGallery={condensedGallery}
        onClick={
          validZoomIconValue || videoEnhancements ? showZoomModal : undefined
        }
        showZoomIconOnHover={amendCarouselZoomIcon === "noIcon"}
        socialProofMessagesProperties={socialProofMessagesProperties}
      >
        {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
          <ErrorBoundary componentName="SocialProofMessages">
            <SocialProofMessages
              {...socialProofMessagesProperties}
              className={
                useNewSocialProofMessagesPosition
                  ? "carouselGalleryInstanceNewPosition"
                  : undefined
              }
            />
          </ErrorBoundary>
        )}
        {mediaItems.length > 1 && !condensedGallery && (
          <Counter selectedInd={selectedInd} itemCount={mediaItems.length} />
        )}
        {mediaItems[selectedInd]?.type === "image" &&
          !validZoomIconValue &&
          (condensedGallery ? (
            <button
              className={styles.zoomButtonCondensed}
              onClick={showZoomModal}
              data-testid="gallery:zoom:button"
            >
              <MagnifyOutlined size="medium" />
            </button>
          ) : (
            <button
              className={styles.zoomButton}
              onClick={showZoomModal}
              data-testid="gallery:zoom:button"
            >
              <ZoomCrossOutlined className={styles.zoomIcon} size="medium" />
              Zoom
            </button>
          ))}
        {amendCarouselZoomIcon === "newIcon" && (
          <div
            className={`${styles.zoomButton} ${styles.inactiveZoomButton}`}
            data-testid="gallery:zoom:new-icon"
          >
            <MagnifyOutlined size="medium" />
          </div>
        )}
      </Carousel>
      <ZoomModal
        closeZoomModal={closeZoomModal}
        mediaItems={[
          ...outputImages,
          ...(showVideosOnModal || videoEnhancements ? outputVideos : []),
        ]}
        selectedIndex={modalSelectedInd}
        setSelectedIndex={setModalSelectedInd}
        hasImmediateStateUpdates={hasImmediateStateUpdates}
        hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
        hasDeferredIntersectionObserverCallback={
          hasDeferredIntersectionObserverCallback
        }
        hasSuperZoom={hasSuperZoom}
        carouselGalleryInpTest={carouselGalleryInpTest}
        videoPreload={videoPreload}
        isZoomModalOpen={zoomModalActive}
        videoAutoplayOnLoad={videoEnhancements}
      />
    </>
  );

  return renderSection ? (
    <section>
      <h2 className={styles.visuallyHidden}>Image gallery</h2>
      {carouselContent}
    </section>
  ) : (
    carouselContent
  );
};
